// PushSoftAsk — the D-17 in-page card that precedes the browser's push permission prompt.
//
// Never rendered on page load by itself: callers place it after a meaningful action (a posted
// review, a submitted claim, the first bell open). It then decides on its own whether to show:
// push supported, permission still "default", no subscription on this device, and the prompt not
// dismissed in the last 30 days (shouldShowPushSoftAsk). Everything browser-side is read in an
// effect, so the server render is always empty, and the embedded preview iframe never shows it.
//
// The native prompt fires ONLY from the accept click: the handler calls subscribeToPush() with no
// await before it, which keeps the click's user activation alive for the permission request.
// On iOS outside a Home Screen install the card is the install instruction instead and never
// requests anything.
//
// All copy comes from PUSH_SOFT_ASK_COPY (shared), so this file holds no Arabic literals.

import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing } from "lucide-react";
import { toast } from "sonner";
import { PREFERENCES_COPY, PUSH_SOFT_ASK_COPY } from "@shared/notificationCopy";
import { useAuth } from "@/contexts/AuthContext";
import { usePostHogTracking, type PushPermissionSurface } from "@/hooks/usePostHogTracking";
import { dismissPushPrompt, getNotificationPreferences } from "@/api/notificationService";
import { isEmbeddedPreview } from "@/lib/embeddedPreview";
import { NOTIFICATION_QUERY_KEYS } from "@/lib/notificationUi";
import {
  getDeviceSubscription,
  getPermission,
  getPushSupport,
  shouldShowPushSoftAsk,
  subscribeToPush,
} from "@/lib/pushSubscription";

export type PushSoftAskSurface = Exclude<PushPermissionSurface, "preferences">;

type CardState = "pending" | "ask" | "ios_install" | "denied" | "hidden";

const font = "var(--ys-font)";

const cardStyle: React.CSSProperties = {
  background: "var(--ys-paper)",
  border: "1px solid var(--ys-rule)",
  borderRadius: "var(--ys-r)",
  padding: 16,
  direction: "rtl",
  fontFamily: font,
  textAlign: "start",
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
};

const titleStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 16,
  fontWeight: 600,
  color: "var(--ys-ink)",
  margin: 0,
};

const bodyStyle: React.CSSProperties = {
  fontFamily: font,
  fontSize: 14,
  fontWeight: 400,
  color: "var(--ys-ink-2)",
  margin: "4px 0 0",
};

const acceptBtnStyle: React.CSSProperties = {
  background: "var(--ys-primary)",
  color: "#fff",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 16px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

const ghostBtnStyle: React.CSSProperties = {
  background: "transparent",
  color: "var(--ys-ink-2)",
  border: "none",
  borderRadius: "var(--ys-r)",
  padding: "8px 12px",
  minHeight: 44,
  fontFamily: font,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
};

/** Same cache entry the preferences matrix and WhatsApp card read: the unwrapped preferences. */
const fetchPreferences = async () => {
  const res = await getNotificationPreferences();
  if (!res.ok) throw new Error(res.error);
  return res.data;
};

export default function PushSoftAsk({ surface, onDone }: { surface: PushSoftAskSurface; onDone?: () => void }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { trackNotifications } = usePostHogTracking();
  const [state, setState] = useState<CardState>("pending");
  const [busy, setBusy] = useState(false);
  // Once the user acted (or we decided to hide), later preference refetches never re-open it.
  const settled = useRef(false);

  const prefs = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.preferences,
    queryFn: fetchPreferences,
    enabled: !!user,
    retry: false,
  });

  useEffect(() => {
    if (settled.current) return;
    if (!user || prefs.isError) {
      if (prefs.isError) {
        settled.current = true;
        setState("hidden");
      }
      return;
    }
    if (!prefs.data) return;
    if (isEmbeddedPreview()) {
      settled.current = true;
      setState("hidden");
      return;
    }

    let cancelled = false;
    const dismissedAt = prefs.data.prompts?.pushPromptDismissedAt ?? null;
    (async () => {
      const support = getPushSupport();
      const subscribed = support === "supported" ? !!(await getDeviceSubscription()) : false;
      if (cancelled || settled.current) return;
      const next = shouldShowPushSoftAsk({ support, permission: getPermission(), subscribed, dismissedAt });
      settled.current = true;
      setState(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, prefs.data, prefs.isError]);

  const close = () => {
    settled.current = true;
    setState("hidden");
    onDone?.();
  };

  const handleAccept = async () => {
    // No await before this call: the permission prompt needs the click's user activation.
    const pending = subscribeToPush();
    setBusy(true);
    const result = await pending;
    setBusy(false);
    settled.current = true;
    if (result === "granted") {
      toast.success(PUSH_SOFT_ASK_COPY.grantedToast);
      trackNotifications.pushPermissionGranted({ surface });
      void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.preferences });
      close();
      return;
    }
    if (result === "denied") {
      // Replaced by the denied line for the rest of this session (the component's lifetime).
      setState("denied");
      return;
    }
    if (result === "error") {
      toast.error(PREFERENCES_COPY.saveFailedToast);
      return;
    }
    close();
  };

  const handleDismiss = () => {
    void dismissPushPrompt();
    void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.preferences });
    close();
  };

  if (state === "pending" || state === "hidden") return null;

  if (state === "denied") {
    return (
      <div dir="rtl" style={cardStyle} role="status">
        <p style={{ ...bodyStyle, margin: 0 }}>{PUSH_SOFT_ASK_COPY.deniedLine}</p>
      </div>
    );
  }

  const ios = state === "ios_install";
  const copy = ios ? PUSH_SOFT_ASK_COPY.surfaces.ios_install : PUSH_SOFT_ASK_COPY.surfaces[surface];

  return (
    <div dir="rtl" style={cardStyle}>
      <BellRing size={20} aria-hidden style={{ color: "var(--ys-primary)", flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={titleStyle}>{copy.title}</p>
        <p style={bodyStyle}>{copy.body}</p>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {ios ? (
            <button type="button" style={acceptBtnStyle} onClick={handleDismiss}>
              {PUSH_SOFT_ASK_COPY.surfaces.ios_install.button}
            </button>
          ) : (
            <>
              <button
                type="button"
                style={{ ...acceptBtnStyle, opacity: busy ? 0.7 : 1 }}
                disabled={busy}
                onClick={() => {
                  void handleAccept();
                }}
              >
                {PUSH_SOFT_ASK_COPY.accept}
              </button>
              <button type="button" style={ghostBtnStyle} disabled={busy} onClick={handleDismiss}>
                {PUSH_SOFT_ASK_COPY.dismiss}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
